import React, { useState } from 'react';
import {
  BaseRecord,
  CrudFilters,
  IResourceComponentsProps,
  useExport,
  useNavigation,
  useTranslate,
} from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Button, Popover, Table, Tooltip, message } from 'antd';
import {
  DownloadOutlined,
  LineOutlined,
  RedoOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { ConsumerRes } from '../../types';
import { List } from '@components/crud/consumerList';
import { SearchContent } from '@components/SearchConsumer';
import AddDoublePointsModal from '@components/AddDoublePointsModal';
import DoublePointsList from '@components/DoublePointsList';
import { ColHeaderRight } from '@components/ColHeaderRight';
import { formatDate, numberWithCommas } from '../../Helpers';
import { apiRequest } from '../../rest-data-provider/utils/APIs';
import { SortPopover } from '@components/SortPopover';
import { sortBy } from 'lodash';

export const ConsumerList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [data] = useState<ConsumerRes[]>([]);
  const [usersData, setUsersData] = useState<ConsumerRes[]>([]);
  const [updatedDouplePoints, setUpdatedDouplePoints] = useState<number>(0);
  const [sortedData, setSortedData] = useState<ConsumerRes[]>([]);
  const {
    searchFormProps,
    tableProps,
    setFilters,
    filters,
    tableQueryResult: { refetch },
  } = useTable({
    resource: 'account-cruds',
    filters: {
      initial: [{ field: 'account_type', operator: 'eq', value: 'consumer' }],
    },
    onSearch: (values: {
      consumer_id: number;
      consumer_name: string;
      consumer_phone: number;
      consumer_email: string;
      min_price: number;
      max_price: number;
      min_point: number;
      max_point: number;
    }) => {
      const filters: CrudFilters = [];
      if (values.consumer_id) {
        filters.push({
          field: 'national_id',
          operator: 'eq',
          value: values.consumer_id,
        });
      }
      if (values.consumer_name) {
        filters.push({
          field: 'name',
          operator: 'eq',
          value: values.consumer_name,
        });
      }
      if (values.consumer_phone) {
        filters.push({
          field: 'phone_number',
          operator: 'eq',
          value: values.consumer_phone,
        });
      }
      if (values.consumer_email) {
        filters.push({
          field: 'email',
          operator: 'eq',
          value: values.consumer_email,
        });
      }
      if (values.min_point || values.max_point) {
        filters.push({
          field: 'fromPoints',
          operator: 'eq',
          value: values.min_point,
        });
        filters.push({
          field: 'toPoints',
          operator: 'eq',
          value: values.max_point,
        });
      }
      if (values.min_price || values.max_price) {
        filters.push({
          field: 'fromAmount',
          operator: 'eq',
          value: values.min_price,
        });
        filters.push({
          field: 'toAmount',
          operator: 'eq',
          value: values.max_price,
        });
      }
      return filters;
    },
    syncWithLocation: true,
  });
  const { triggerExport, isLoading: exportLoading } = useExport({
    resource: 'account-cruds',
    filters: filters,
    mapData: (item: ConsumerRes) => {
      return {
        ID: item.id,
        Name: item.name,
        Purchases: item.Wallet_balance,
        Points: item.card_details?.AllPoints,
        Phone: item.phone_number,
        Email: item.email,
        CreatedAt: item.created_at ? formatDate(item.created_at) : '',
      };
    },
  });
  const { show } = useNavigation();
  const navigateToShow = (record: BaseRecord) => {
    const consumerRecord = record as ConsumerRes;
    show('account-cruds', consumerRecord.id);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };
  const handleReset = () => {
    searchFormProps.form?.resetFields();
    //@ts-ignore
    setFilters((currentFilters) => {
      return currentFilters.filter(
        (filter: { field: string }) => filter.field === 'account_type'
      );
    }, 'replace');
    refetch();
  };
  const handleSort = (sort: string) => {
    const currentData = tableProps.dataSource as ConsumerRes[];
    let sorted: ConsumerRes[] = [...currentData];

    switch (sort) {
      case 'name':
        sorted = sortBy(currentData, [(item) => item.name?.toLowerCase()]);
        break;
      case 'points:desc':
        sorted = sortBy(currentData, [
          (item) => Number(item.card_details?.AllPoints),
        ]).reverse();
        break;
      case 'points:asc':
        sorted = sortBy(currentData, [
          (item) => Number(item.card_details?.AllPoints),
        ]);
        break;
      default:
        sorted = currentData;
    }

    setSortedData(sorted);
  };
  const getUsers = async () => {
    try {
      const res = await apiRequest<ConsumerRes[]>({
        url: 'account-cruds',
        method: 'GET',
        params: {
          account_type: 'consumer',
          is_double_points: 'true',
        },
      });
      setUsersData(res.data);
    } catch (error) {
      message.error(translate('pages.errors.error'));
    }
  };

  const handleSyncBalances = async () => {
    try {
      apiRequest({
        url: '/account-cruds/sync/balances',
        method: 'GET',
      })
        .then(() => {
          message.success(translate('account-cruds.syncSuccess'));
        })
        .catch(() => {
          message.error(translate('account-cruds.syncError'));
        });
      message.info(translate('account-cruds.syncing'));
    } catch (error) {}
  };

  return (
    <List
      title={false}
      breadcrumb={
        <div className="flex flex-row gap-y-2 flex-wrap justify-between ">
          <div className="flex flex-row justify-between  px-4">
            <div className="text-primary font-bold text-nowrap text-base leading-8">
              {translate('account-cruds.account-cruds')}
            </div>
            <AddDoublePointsModal
              consumers={tableProps.dataSource as ConsumerRes[]}
              data={data}
              updatedDouplePoints={updatedDouplePoints}
              setDoublePoints={setUpdatedDouplePoints}
              getUsers={getUsers}
            />
            <DoublePointsList
              updatedDouplePoints={updatedDouplePoints}
              setUpdatedDouplePoints={setUpdatedDouplePoints}
              usersData={usersData}
              getUsers={getUsers}
            />
          </div>
          <div className="flex gap-x-2">
            <Popover
              arrow={false}
              open={open}
              content={<SearchContent {...searchFormProps} />}
              onOpenChange={handleOpenChange}
              className="w-full"
            >
              <Button
                icon={<SearchOutlined />}
                className="shadow-none h-10"
                iconPosition="end"
                size="large"
              >
                {translate('account-cruds.search')}
              </Button>
            </Popover>
            <Tooltip title={translate('account-cruds.reset')} trigger={'hover'}>
              <Button onClick={handleReset} className=" h-10">
                <RedoOutlined />
              </Button>
            </Tooltip>
            <Button
              className="h-10 rounded-xl px-2"
              icon={<DownloadOutlined />}
              type="primary"
              onClick={triggerExport}
              loading={exportLoading}
              iconPosition="end"
            >
              {translate('account-cruds.download')}
            </Button>
            <Button
              className="h-10 rounded-xl px-2"
              icon={<SyncOutlined />}
              type="primary"
              onClick={handleSyncBalances}
              iconPosition="end"
            >
              {translate('account-cruds.syncBalances')}
            </Button>
          </div>
        </div>
      }
    >
      <SortPopover handleSort={handleSort} />
      <Table
        {...tableProps}
        dataSource={sortedData.length > 0 ? sortedData : tableProps.dataSource}
        rowKey="id"
        className="table w-full mt-12"
        bordered={false}
        pagination={{
          position: ['bottomCenter'],
          nextIcon: <></>,
          prevIcon: <></>,
        }}
        onRow={(record) => {
          return {
            onClick: () => navigateToShow(record),
          };
        }}
      >
        <Table.Column
          className="table-header"
          title={<ColHeaderRight title={translate('account-cruds.id')} />}
          dataIndex="id"
          render={(value) => <span>{value ? value : <LineOutlined />}</span>}
        />
        <Table.Column
          className="table-header"
          title={<ColHeaderRight title={translate('account-cruds.name')} />}
          dataIndex="name"
          render={(value) => <span>{value ? value : <LineOutlined />}</span>}
        />
        <Table.Column
          className="table-header"
          title={translate('account-cruds.purchases')}
          dataIndex="WalletBalance"
          render={(value) => (
            <span className="flex justify-center">
              {value ? numberWithCommas(value) : <LineOutlined />}
            </span>
          )}
        />
        <Table.Column
          className="table-header"
          title={translate('account-cruds.points')}
          dataIndex={['card_details', 'PointsEarned']}
          render={(value) => (
            <span className="flex justify-center">
              {`${numberWithCommas(`${+value / 10}`)} ${translate('account-cruds.onePoint')}`}
            </span>
          )}
        />
        <Table.Column
          className="table-header"
          title={<ColHeaderRight title={translate('account-cruds.phone')} />}
          dataIndex="phone_number"
          render={(value) => <span>{value ? value : <LineOutlined />}</span>}
        />
        <Table.Column
          className="table-header ant-table-cell table-header"
          title={translate('account-cruds.email')}
          dataIndex="email"
          render={(value) => <span>{value ? value : <LineOutlined />}</span>}
        />
        <Table.Column
          className="table-header"
          title={translate('account-cruds.createdAt')}
          dataIndex="created_at"
          render={(value) => (
            <span>{value ? formatDate(value) : <LineOutlined />}</span>
          )}
        />
      </Table>
    </List>
  );
};
